/*
 * notify-me.js: the "Coming Soon" back-in-stock form on product pages.
 * Round 6, KD-20 (privacy P0), BUILD_MAP contract C6.
 *
 * Why this file exists
 * - bundle.js binds a submit handler to each notify form once, at load (initProductNotification).
 *   A colour swap replaces the product section, and with it both forms, and the bundle never
 *   re-binds them. The new form then submitted natively: <form action="#"> is a GET, so her email
 *   and phone went into the address bar (?email=…&phone=…), where every page-view tag could read them.
 * - The bundle also showed "Thanks for subscribing" before Klaviyo answered, and could only
 *   subscribe a size of the shade on the page (it matches the size text against the page's variants).
 *
 * What it does
 * - One capture-phase submit listener on document. It runs before any listener on the form,
 *   cancels the native submit and stops the event there, so the bundle's handler never runs and no
 *   field reaches the URL, before or after a swap. The Liquid also gives both forms method="post"
 *   and onsubmit="return false;" in case this file never loads.
 * - reportValidity() is a gate. The variant is form.dataset.variantId, set by whoever opened the
 *   sheet (SofsyNotify.open below); when it is set but not a numeric id, the error message shows and
 *   nothing is sent. Only a sheet opened by the bundle's own sold-out labels (no id set) uses today's
 *   lookup: the size in .oos-size matched against the page's variant-radios JSON.
 * - The POST is today's request: Klaviyo's back-in-stock subscription API (no list, no form id;
 *   master correction 10, zao#218), revision 2024-07-15, variant "$shopify:::$default:::<id>".
 *   With a phone: email and phone, channels EMAIL and SMS, as before.
 * - The phone is OPTIONAL (zao#612, Ben 7 Oct: on sofsy.com at phone width the sheet refused to send without one,
 *   "Please fill out this field", news box unticked). Left blank: the request carries the email only, channels EMAIL
 *   only, no phone_number and no SMS. Typed but not a number we can read: the phone message, nothing sent, as before.
 *   Both sheets (phone and desktop) are the same form, so both behave the same; the Liquid no longer marks the field
 *   required, and reset() below also clears `required` from a sheet an older cached page still draws with it.
 * - "Thanks for subscribing" shows only on a 2xx. A 4xx naming the phone shows the phone message,
 *   anything else (including a network failure or 15 s without an answer) shows the error message.
 *
 * News box and bot trap (zao#617, Ben 5 Oct):
 * - "Also send me sofsy news and offers" (input[name="news_opt_in"]), unticked on every open. Unticked: nothing differs
 *   from before. Ticked (as she sent, and still when Klaviyo answers): after the back-in-stock request above answers
 *   2xx (never before, never on its own), the same email is also sent to Klaviyo's client subscription API, onto
 *   list XdwVYY (the footer sign-up's list and request, assets/footer-signup.js): custom_source "Notify me", email
 *   marketing consent only (never the phone, never SMS), profile properties signup_source "notify_me" and
 *   signup_product_handle (the page's product).
 *   XdwVYY is double opt-in, so Klaviyo sends its one confirmation email. That second request never changes what the
 *   sheet shows: the back-in-stock answer alone decides the message, as before.
 * - The trap: input[name="company"] that people never see or reach, and a minimum of 1 s from the sheet opening (or
 *   her first touch of the form, whichever is earlier) to the send. A filled trap shows the usual "Thanks for
 *   subscribing" and sends NOTHING, neither request; so does a form nobody opened or touched (no clock at all). They
 *   are reported as pdp.notify_form.blocked {reason: 'honeypot' | 'too_fast'}, never as notify.request.success. A
 *   send under 1 s with the trap empty is never dropped: the button shows its usual sending state, the send waits out
 *   the rest of the 1 s, then goes as normal, with no blocked event (a shopper who taps the keyboard's email
 *   suggestion can be that quick). Her own mistakes (email, phone) are checked first, so a person who is quick still
 *   sees why nothing happened.
 *
 * Look: today's sheet, unchanged. A message sits where the fields are, so the fields fade out while
 * it shows (as today). After an error the fields come back after 3 s (the build's readable-message
 * hold), with what she typed, so she can correct it and send again.
 *
 * For later steps (P09 sold-out pill, drawer G0 "Notify me"): open the sheet with
 *   window.SofsyNotify.open({ variantId, size, shade, opener })
 *   variantId  REQUIRED. The numeric id of the variant to subscribe, any shade of the style: Liquid
 *              {{ variant.id }}, as a number or a string of digits. Not a GID, not a product id.
 *   size       the size words shown in the question ("XS-S")
 *   shade      the shade's display name, any shade of the style; the question always names it (A17),
 *              the page's shade when none is given
 *              ("… when Midnight Black in size XS-S becomes available?")
 *   opener     the element that opened it; focus returns there when the sheet closes. Pass a
 *              focusable element (a <button>).
 *   Returns true when the sheet opened. A missing or unreadable variantId (a GID, undefined from a
 *   missing data attribute, a typo) opens nothing, logs a console warning and returns false: guessing
 *   from the size words would subscribe the page's shade instead of hers and still say "Thanks".
 * Never give this script the script-reload class: the bundle re-requests those after every colour swap.
 */
(() => {
  'use strict';

  if (window.SofsyNotify) return;

  const ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=VMQtQt';
  const REVISION = '2024-07-15';
  const TIMEOUT_MS = 15000;
  const MESSAGE_HOLD_MS = 3000;
  const AUTO_CLOSE_MS = 3000; // A17: "Thanks for subscribing" holds this long, then the sheet closes itself
  const CLOSE_CLEANUP_MS = 500;
  // zao#617: the news box's request (assets/footer-signup.js's request, its own source) and the bot trap.
  const NEWS_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions?company_id=VMQtQt';
  const NEWS_REVISION = '2026-07-15';
  const NEWS_LIST_ID = 'XdwVYY';
  const NEWS_CUSTOM_SOURCE = 'Notify me';
  const NEWS_SIGNUP_SOURCE = 'notify_me';
  const MIN_FILL_MS = 1000; // 1 s, as the footer sign-up: a shopper who autofills and taps must never be lost

  const MODAL = {
    mobile: '#PopupModal-productDrawer-mobile-notify',
    desktop: '#PopupModal-productDrawer',
  };
  const OPEN_EVENT = { mobile: 'mobileNotifyMeOpened', desktop: 'desktopNotifyMeOpened' };
  const FORMS = `${MODAL.mobile} .notify-me-modal form, ${MODAL.desktop} .notify-me-modal form`;
  const MESSAGE = { success: '.success-message', error: '.error-message', phone: '.phone-message' };

  const holdTimers = new WeakMap();
  let requestSeq = 0;

  const isNotifyForm = (el) => el instanceof HTMLFormElement && el.matches(FORMS);
  const formIn = (modal) => (modal ? modal.querySelector('.notify-me-modal form') : null);
  const fields = (form) => form.querySelector('.product-popup-modal__content-info');
  const submitButton = (form) => form.querySelector('button.button--primary') || form.querySelector('button');
  const newsBox = (form) => form.querySelector('input[name="news_opt_in"]');
  const trapField = (form) => form.querySelector('input[name="company"]');
  const now = () => (window.performance && typeof window.performance.now === 'function' ? window.performance.now() : Date.now());

  /* ---------- tracking (taxonomy v2.0 r6.1, spec 3.6) ----------
   * notify.request.success {channel, place, shade, size, variant_id} when Klaviyo answers 2xx; notify.request.fail
   * {reason, place}: invalid_email / invalid_phone (the form's own check, or Klaviyo naming the phone), network (no
   * answer, or 15 s), api (any other refusal), other (no variant to subscribe). One per answer. channel is 'both'
   * when she gave a phone (EMAIL and SMS), 'email' when she left it blank (EMAIL only; zao#612). place: drawer | page (who called SofsyNotify.open) | legacy_modal (a
   * sheet the bundle's own sold-out labels opened). Never the email, the phone or any text she typed.
   * pdp.notify_form.blocked {reason: 'honeypot' | 'too_fast'} when the bot trap kept a send back (zao#617): the reason
   * only, never the trap's contents. 'too_fast' now means only a form nobody opened or touched; a quick person with
   * the trap empty waits out the 1 s and is sent, never blocked. */
  const track = (name, props) => {
    try {
      if (typeof window.sofsyTrack === 'function') window.sofsyTrack(name, props || {});
    } catch (_) {
      /* tracking never breaks the sheet */
    }
  };
  const placeOf = (form) => form.dataset.notifyPlace || 'legacy_modal';
  const trackFail = (form, reason) => track('notify.request.fail', { reason, place: placeOf(form) });

  /* ---------- variant ---------- */

  const warn = (message) => {
    if (window.console && typeof window.console.warn === 'function') window.console.warn(`SofsyNotify: ${message}`);
  };

  // A Shopify variant id: a positive whole number. Accepts a JS number (only while it is exact, so a
  // rounded id can never be sent) or a string of digits; anything else, a GID included, is null.
  const VARIANT_ID = /^[1-9]\d*$/;
  const toVariantId = (value) => {
    if (typeof value === 'number') return Number.isSafeInteger(value) && value > 0 ? String(value) : null;
    if (typeof value !== 'string') return null;
    const id = value.trim();
    return VARIANT_ID.test(id) ? id : null;
  };

  const pageVariants = () => {
    try {
      const radios = document.querySelector('variant-radios');
      const json = radios && radios.querySelector('[type="application/json"]');
      const list = json ? JSON.parse(json.textContent) : [];
      return Array.isArray(list) ? list : [];
    } catch (_) {
      return [];
    }
  };

  const resolveVariantId = (form) => {
    // An id set by whoever opened the sheet is the only answer: when it cannot be read, the submit
    // shows the error message rather than guess a variant (the guess is the page's shade, not hers).
    if ('variantId' in form.dataset) {
      const explicit = toVariantId(form.dataset.variantId);
      if (!explicit) warn(`the sheet's variant id "${form.dataset.variantId}" is not a numeric variant id; nothing was sent.`);
      return explicit;
    }
    // Today's lookup (the bundle's own sold-out labels): the size words in the question against the
    // page's own variants.
    const sizeEl = form.querySelector('.oos-size');
    const size = sizeEl ? sizeEl.textContent.trim() : '';
    if (!size) return null;
    const match = pageVariants().find((v) => v && String(v.title).trim() === size);
    return match ? toVariantId(String(match.id)) : null;
  };

  /* ---------- phone ---------- */

  // The country select's value is "+1", "+44", "+1-268" …. The field takes any format (+1, spaces, brackets, dots, dashes:
  // autofill sends "+447700900123"); only the digits are read.
  // Returns E.164 ("+12015550123", Klaviyo's documented format) or null. Klaviyo validates again
  // server-side; a 4xx naming the phone shows the same phone message.
  //
  // 1. Typed with a leading "+": those digits ARE the whole international number and the select is ignored (the select
  //    says +44, she typed "+44 7700 900123": that is +447700900123, never +44447700900123). Valid when it is a NANP
  //    number (11 digits, 1[2-9]xx[2-9]xxxxxx) or E.164-shaped (8 to 15 digits, first digit not 0).
  // 2. No "+": the select's code goes in front of the digits, exactly as before. Nothing tries to guess that a code was
  //    typed twice: a national number can legitimately begin with its own country's code (+91 9123456789, +7 701 234
  //    5678, +49 0491 123456), and no length separates those from a doubled code. Autofill sends "+cc…", which rule 1 reads.
  const toE164 = (code, raw) => {
    const text = String(raw || '').trim();
    if (text.charAt(0) === '+') {
      const intl = text.replace(/\D/g, '');
      if (intl.charAt(0) === '1') return /^1[2-9]\d{2}[2-9]\d{6}$/.test(intl) ? `+${intl}` : null;
      return /^[1-9]\d{7,14}$/.test(intl) ? `+${intl}` : null;
    }
    const cc = String(code || '').replace(/\D/g, '');
    let nn = text.replace(/\D/g, '').replace(/^0+/, '');
    if (!cc || !nn) return null;
    if (cc.charAt(0) === '1') {
      // North American Numbering Plan: +1, and the +1-xxx islands whose area code is in the select.
      if (cc.length === 1 && nn.length === 11 && nn.charAt(0) === '1') nn = nn.slice(1);
      if (cc.length === 4 && nn.length === 10 && nn.indexOf(cc.slice(1)) === 0) nn = nn.slice(3);
      const full = cc + nn;
      return /^1[2-9]\d{2}[2-9]\d{6}$/.test(full) ? `+${full}` : null;
    }
    const full = cc + nn;
    return nn.length >= 5 && full.length <= 15 ? `+${full}` : null;
  };

  /* ---------- states ---------- */

  const liveRegion = (form) => {
    let live = form.querySelector('[data-notify-live]');
    if (!live) {
      live = document.createElement('p');
      live.className = 'visually-hidden';
      live.setAttribute('role', 'status');
      live.setAttribute('data-notify-live', '');
      form.appendChild(live);
    }
    return live;
  };

  const announce = (form, text) => {
    const live = liveRegion(form);
    live.textContent = '';
    if (text) window.setTimeout(() => { live.textContent = text; }, 60);
  };

  const hideMessages = (form) => {
    form.querySelectorAll('.errorMessage').forEach((m) => {
      m.style.opacity = 0;
      m.setAttribute('aria-hidden', 'true');
    });
  };

  const setFieldsShown = (form, shown) => {
    [fields(form), submitButton(form)].forEach((el) => {
      if (!el) return;
      el.style.opacity = shown ? '' : '0';
      if (shown) el.removeAttribute('inert');
      else el.setAttribute('inert', '');
    });
  };

  const sheetIsOpen = (form) => {
    const modal = form.closest('modal-dialog');
    return !!(form.isConnected && modal && modal.hasAttribute('open'));
  };

  // Focus stays inside the open sheet while parts of it are disabled or faded (they would drop it to <body>).
  const keepFocusInSheet = (form) => {
    const dialog = form.closest('[role="dialog"]');
    const active = document.activeElement;
    if (!dialog || !sheetIsOpen(form)) return;
    if (!active || active === document.body || form.contains(active)) dialog.focus({ preventScroll: true });
  };

  const setBusy = (form, busy) => {
    const button = submitButton(form);
    if (busy) {
      form.dataset.notifyBusy = 'true';
      form.setAttribute('aria-busy', 'true');
      if (button && document.activeElement === button) keepFocusInSheet(form);
      if (button) button.disabled = true;
    } else {
      delete form.dataset.notifyBusy;
      form.removeAttribute('aria-busy');
      if (button) button.disabled = false;
    }
  };

  const show = (form, kind) => {
    window.clearTimeout(holdTimers.get(form));
    keepFocusInSheet(form);
    setFieldsShown(form, false);
    hideMessages(form);
    const message = form.querySelector(MESSAGE[kind]);
    if (message) {
      message.style.opacity = 1;
      message.removeAttribute('aria-hidden');
    }
    announce(form, message ? message.textContent.trim() : '');
    if (kind === 'success') {
      form.dataset.notifyDone = 'true';
      // A17 (Ben, MC-39): "after a few more seconds, can you close the drawer automatically?" Through the sheet's
      // own close button, as her tap would (focus goes back to what opened it). Reopening clears the timer (reset).
      holdTimers.set(form, window.setTimeout(() => {
        if (!sheetIsOpen(form) || form.dataset.notifyDone !== 'true') return;
        const modal = form.closest('modal-dialog');
        const close = modal && modal.querySelector('[id^="ModalClose-productDrawer"]');
        if (close) close.click();
      }, AUTO_CLOSE_MS));
      return;
    }
    holdTimers.set(form, window.setTimeout(() => {
      hideMessages(form);
      setFieldsShown(form, true);
      const fix = kind === 'phone' ? form.querySelector('input[name="phone"]') : submitButton(form);
      if (fix && sheetIsOpen(form)) fix.focus({ preventScroll: true });
    }, MESSAGE_HOLD_MS));
  };

  // A17 (Ben, MC-39: "it doesnt say the colour name"): the question always names the shade, her own shade on the
  // page included ("… when Midnight Black in size XL-2XL becomes available?"). The page's shade is the fallback:
  // the sheet the bundle's own sold-out labels open is always for the page's shade.
  const pageShade = () => {
    const frame = document.querySelector('#main-product [data-pdp-cs]');
    return frame ? String(frame.dataset.shade || '').trim() : '';
  };

  const setShade = (form, shade) => {
    const span = form.querySelector('[data-notify-shade]');
    if (!span) return;
    const name = String(shade || '').trim() || pageShade();
    if (name) {
      const template = span.dataset.template || '';
      span.textContent = `${template.indexOf('%shade%') > -1 ? template.replace('%shade%', name) : name} `;
      span.hidden = false;
      form.dataset.shade = name;
    } else {
      span.textContent = '';
      span.hidden = true;
      delete form.dataset.shade;
    }
  };

  // A fresh sheet every time it opens, whoever opened it (the bundle's own size labels included).
  const reset = (form) => {
    if (!form) return;
    window.clearTimeout(holdTimers.get(form));
    delete form.dataset.notifyRequest;
    delete form.dataset.notifyDone;
    delete form.dataset.variantId;
    delete form.dataset.notifyPlace;
    delete form.dataset.notifySize;
    // zao#617: the news box starts unticked and the trap empty on every open; the 1 s clock starts now.
    const box = newsBox(form);
    if (box) box.checked = false;
    const trap = trapField(form);
    if (trap) trap.value = '';
    delete form.dataset.notifyTouchedAt;
    form.dataset.notifyOpenedAt = String(now() || 1);
    // zao#612: the phone is optional, also on a sheet an older cached page still draws with `required`.
    const phoneField = form.querySelector('input[name="phone"]');
    if (phoneField) {
      phoneField.removeAttribute('required');
      phoneField.removeAttribute('aria-required');
      // Any format she types or her browser autofills (+1, spaces, brackets, dots); toE164 reads it. An older cached page
      // still draws a digits-and-dashes `pattern`, whose generic browser message would block it before we can read it.
      phoneField.removeAttribute('pattern');
    }
    setBusy(form, false);
    hideMessages(form);
    setFieldsShown(form, true);
    setShade(form, '');
    form.querySelectorAll('.field--with-error').forEach((f) => f.classList.remove('field--with-error'));
    liveRegion(form).textContent = '';
  };

  /* ---------- submit ---------- */

  const send = (payload) => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? window.setTimeout(() => controller.abort(), TIMEOUT_MS) : null;
    return window.fetch(ENDPOINT, {
      method: 'POST',
      headers: { accept: 'application/json', revision: REVISION, 'content-type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller ? controller.signal : undefined,
    }).finally(() => window.clearTimeout(timer));
  };

  // zao#617: the news box's request. Only after a 2xx from the back-in-stock request, and only when the box was ticked
  // as she sent and still is. Its answer changes nothing on screen (the sheet already says "Thanks"); a failure is
  // swallowed. Email marketing consent only: no phone, no SMS.
  const PRODUCT_HANDLE = /^[a-z0-9][a-z0-9-]{0,254}$/;
  const subscribeNews = (email, handle) => {
    try {
      const properties = { signup_source: NEWS_SIGNUP_SOURCE };
      if (typeof handle === 'string' && PRODUCT_HANDLE.test(handle)) properties.signup_product_handle = handle;
      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: NEWS_CUSTOM_SOURCE,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                  properties,
                },
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: NEWS_LIST_ID } } },
        },
      };
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = controller ? window.setTimeout(() => controller.abort(), TIMEOUT_MS) : null;
      return window.fetch(NEWS_ENDPOINT, {
        method: 'POST',
        headers: { accept: 'application/vnd.api+json', revision: NEWS_REVISION, 'content-type': 'application/vnd.api+json' },
        body: JSON.stringify(payload),
        signal: controller ? controller.signal : undefined,
      })
        .catch(() => null)
        .finally(() => window.clearTimeout(timer));
    } catch (_) {
      return null; // the news sign-up never breaks the sheet
    }
  };

  // zao#617: when the 1 s clock started: the sheet opening or her first touch of the form, whichever is earlier.
  // 0 when neither happened.
  const clockStart = (form) => {
    const starts = [Number(form.dataset.notifyOpenedAt), Number(form.dataset.notifyTouchedAt)].filter((t) => Number.isFinite(t) && t > 0);
    return starts.length ? Math.min.apply(null, starts) : 0;
  };
  // zao#617: 'honeypot' | 'too_fast' | null. A filled trap, or a form nobody opened or touched (no clock at all).
  // Speed alone never blocks: a quick send with the trap empty waits out the 1 s (submit), then goes.
  const botReason = (form) => {
    const trap = trapField(form);
    if (trap && trap.value !== '') return 'honeypot';
    if (!clockStart(form)) return 'too_fast';
    return null;
  };

  const errorPointers = (response) => response.json()
    .then((body) => ((body && body.errors) || []).map((e) => (e && e.source && e.source.pointer) || '').join(' '))
    .catch(() => '');

  const submit = (form) => {
    if (form.dataset.notifyBusy === 'true' || form.dataset.notifyDone === 'true') return;
    if (!form.reportValidity()) {
      const emailField = form.querySelector('input[name="email"]');
      trackFail(form, emailField && emailField.validity && !emailField.validity.valid ? 'invalid_email' : 'invalid_phone');
      return;
    }

    const variantId = resolveVariantId(form);
    if (!variantId) {
      trackFail(form, 'other');
      show(form, 'error');
      return;
    }
    // zao#612: the phone is optional. Blank: email only. Typed: it must read as a number, or the phone message shows.
    const select = form.querySelector('select[name="countryCode"]');
    const phoneInput = form.querySelector('input[name="phone"]');
    const typedPhone = phoneInput ? String(phoneInput.value || '').trim() : '';
    const phone = typedPhone ? toE164(select && select.value, typedPhone) : null;
    if (typedPhone && !phone) {
      trackFail(form, 'invalid_phone');
      show(form, 'phone');
      return;
    }
    const emailInput = form.querySelector('input[name="email"]');
    const email = emailInput ? emailInput.value.trim() : '';

    // zao#617: a bot gets the usual thank-you, and nothing is sent.
    const blocked = botReason(form);
    if (blocked) {
      track('pdp.notify_form.blocked', { reason: blocked });
      show(form, 'success');
      return;
    }
    // Ticked as she sent, and still ticked when Klaviyo answers: un-ticking while the request is out is a "no".
    const box = newsBox(form);
    const wantsNews = !!(box && box.checked);
    const handle = form.dataset.productHandle || '';

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: { data: { type: 'profile', attributes: phone ? { email, phone_number: phone } : { email } } },
          channels: phone ? ['EMAIL', 'SMS'] : ['EMAIL'],
        },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
        },
      },
    };

    const ticket = String(++requestSeq);
    form.dataset.notifyRequest = ticket;
    setBusy(form, true); // busy from the tap: a second tap while she waits is ignored
    const current = () => form.dataset.notifyRequest === ticket;

    const go = () => send(payload)
      .then((response) => {
        if (!current()) return null;
        if (response.ok) return 'success';
        return errorPointers(response).then((pointers) => (/phone_number/.test(pointers) ? 'phone' : 'api'));
      })
      .catch(() => 'network')
      .then((kind) => {
        if (!current() || !kind) return;
        delete form.dataset.notifyRequest;
        setBusy(form, false);
        if (kind === 'success') {
          track('notify.request.success', {
            channel: phone ? 'both' : 'email',
            place: placeOf(form),
            shade: form.dataset.shade || null,
            size: form.dataset.notifySize || null,
            variant_id: Number(variantId),
          });
          if (wantsNews && box.checked) subscribeNews(email, handle);
        } else {
          trackFail(form, kind === 'phone' ? 'invalid_phone' : kind);
        }
        // api and network show the error message, exactly as before
        show(form, kind === 'api' || kind === 'network' ? 'error' : kind);
      });
    // zao#617: under 1 s from the clock start, wait out the rest, then send. Never dropped for speed. Reopening the
    // sheet during the wait (reset) cancels the ticket, so nothing is sent for the old sheet.
    const left = MIN_FILL_MS - (now() - clockStart(form));
    if (left > 0) window.setTimeout(() => { if (current()) go(); }, left);
    else go();
  };

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!isNotifyForm(form)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    submit(form);
  }, true);

  // The bundle marks empty fields on its own listeners, which a colour swap loses. Same look, delegated.
  document.addEventListener('invalid', (event) => {
    const field = event.target;
    if (field && field.form && isNotifyForm(field.form) && field.parentNode) field.parentNode.classList.add('field--with-error');
  }, true);
  // zao#617: her first touch of the form (focus, typing, autofill) also starts the 1 s clock.
  const touched = (event) => {
    const field = event.target;
    const form = field && field.form;
    if (form && isNotifyForm(form) && !form.dataset.notifyTouchedAt) form.dataset.notifyTouchedAt = String(now() || 1);
  };
  document.addEventListener('focusin', touched, true);
  document.addEventListener('input', touched, true);
  document.addEventListener('input', (event) => {
    const field = event.target;
    if (field && field.form && isNotifyForm(field.form) && field.parentNode && field.checkValidity()) {
      field.parentNode.classList.remove('field--with-error');
    }
  }, true);

  document.addEventListener(OPEN_EVENT.mobile, () => reset(formIn(document.querySelector(MODAL.mobile))));
  document.addEventListener(OPEN_EVENT.desktop, () => reset(formIn(document.querySelector(MODAL.desktop))));

  /* ---------- opening the sheet from new entry points ---------- */

  // Mirrors the bundle's own openers (outOfStockTriggerMobile / outOfStockTrigger), which are not
  // reachable from outside the module, so the sheet looks and closes exactly as it does today.
  const openers = new WeakMap();

  const cleanupAfterClose = (modal) => {
    if (!openers.has(modal)) return;
    const opener = openers.get(modal);
    openers.delete(modal);
    modal.classList.remove('--is-active');
    window.setTimeout(() => {
      modal.style.zIndex = '';
      modal.classList.remove('--is-notify');
    }, CLOSE_CLEANUP_MS);
    if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
  };

  document.addEventListener('click', (event) => {
    const close = event.target instanceof Element ? event.target.closest('[id^="ModalClose-productDrawer"]') : null;
    const modal = close ? close.closest('modal-dialog') : null;
    if (modal) cleanupAfterClose(modal);
  });
  // modal-dialog's hide() (overlay tap, Escape) announces itself on <body>, without bubbling, and
  // only then deactivates its content, so the check waits for hide() to finish.
  const bindModalClosed = () => {
    document.body.addEventListener('modalClosed', () => {
      window.setTimeout(() => {
        Object.values(MODAL).forEach((sel) => {
          const modal = document.querySelector(sel);
          const content = modal && modal.querySelector('.product-popup-modal__content');
          if (modal && content && !content.classList.contains('--is-active')) cleanupAfterClose(modal);
        });
      }, 0);
    });
  };
  if (document.body) bindModalClosed();
  else document.addEventListener('DOMContentLoaded', bindModalClosed);

  const openSheet = (which, modal, size) => {
    const content = modal.querySelector('.product-popup-modal__content');
    const tabs = modal.querySelectorAll('.modal-tab-content');
    tabs.forEach((tab) => {
      if (tab.classList.contains('notify-me-modal')) tab.classList.add('--is-active');
      else if (which === 'desktop') tab.classList.remove('--is-active');
    });
    modal.setAttribute('open', '');
    modal.classList.add('--is-active', '--is-notify');
    if (content) content.classList.add('--is-active');
    modal.style.zIndex = which === 'desktop' ? '99999999999' : '9999999999';
    const sizeEl = modal.querySelector('.notify-me-modal .oos-size');
    if (sizeEl) sizeEl.innerText = size || '';
    document.dispatchEvent(new Event(OPEN_EVENT[which]));
  };

  const open = (options) => {
    const opts = options && typeof options === 'object' ? options : {};
    // Checked before anything opens: a sheet without her variant would subscribe the page's shade.
    const variantId = toVariantId(opts.variantId);
    if (!variantId) {
      let got = typeof opts.variantId;
      try {
        got = typeof opts.variantId === 'number' ? String(opts.variantId) : JSON.stringify(opts.variantId);
        if (got === undefined) got = String(opts.variantId);
      } catch (_) { /* keep the type */ }
      warn(`open() needs the numeric variant id (Liquid variant.id), got ${got}. The sheet was not opened.`);
      return false;
    }
    let which = opts.surface === 'desktop' || opts.surface === 'mobile'
      ? opts.surface
      : (window.matchMedia('(min-width: 1025px)').matches ? 'desktop' : 'mobile');
    let modal = document.querySelector(MODAL[which]);
    if (!formIn(modal) && which === 'desktop') {
      which = 'mobile';
      modal = document.querySelector(MODAL.mobile);
    }
    const form = formIn(modal);
    if (!form) {
      warn('open() found no notify-me sheet on this page. The sheet was not opened.');
      return false;
    }

    openSheet(which, modal, opts.size);
    // After the open event, which resets the sheet, so these are not cleared.
    form.dataset.variantId = variantId;
    setShade(form, opts.shade);
    // For notify.request.*: who opened it, and the size it names (catalogue words from the opener, never typed).
    form.dataset.notifyPlace = opts.opener && typeof opts.opener.closest === 'function' && opts.opener.closest('sofsy-qty-drawer') ? 'drawer' : 'page';
    if (typeof opts.size === 'string' && opts.size.trim()) form.dataset.notifySize = opts.size.trim().slice(0, 32);
    openers.set(modal, opts.opener || null);
    const dialog = modal.querySelector('[role="dialog"]');
    if (dialog) dialog.focus({ preventScroll: true });
    return true;
  };

  window.SofsyNotify = Object.freeze({ open, resolveVariantId, toE164 });
})();
